import { encodeFunctionData, numberToHex, parseEventLogs, type Address, type Hash, type Hex, type Log, type PublicClient, type WalletClient } from 'viem'
import { getRobinhoodPublicClient, ROBINHOOD_CHAIN_HEX, robinhoodViemChain, withTimeout } from '@/lib/chain/publicClient'
import { getPresaleFactoryAddress } from '@/lib/launchpad/contracts'
import { parseEthToWei, weiToEthString } from '@/lib/launchpad/wei'
import { presaleAbi, presaleFactoryAbi } from '@/lib/presale/abi'
import { creatorPctFromLp, PRESALE_LAUNCH_FEE_ETH, PRESALE_START_BUFFER_SECONDS } from '@/lib/protocol'
import type { CreateSimPresaleInput } from '@/lib/presale/types'
import { isOpaqueRpcSimulationError, isUserRejectedError } from '@/lib/walletError'

type Eip1193Request = {
  request: (args: { method: string, params?: unknown[] }) => Promise<unknown>
  providers?: Eip1193Request[]
  provider?: Eip1193Request
  isWalletConnect?: boolean
  isMetaMask?: boolean
  isRabby?: boolean
  isCoinbaseWallet?: boolean
  isBraveWallet?: boolean
  isOkxWallet?: boolean
  isTrust?: boolean
  isRainbow?: boolean
  isPhantom?: boolean
}

function getWindowEthereum(): Eip1193Request | undefined {
  if (typeof window === 'undefined')
    return undefined
  return (window as Window & { ethereum?: Eip1193Request }).ethereum
}

/** AppKit / WalletConnect wrappers still simulate eth_sendTransaction with eth_call. */
export function isAppKitOrWalletConnectProvider(provider?: Eip1193Request | null): boolean {
  if (!provider || typeof provider !== 'object')
    return false
  const record = provider as Record<string, unknown>
  if (provider.isWalletConnect === true)
    return true
  if (record.session && typeof record.session === 'object')
    return true
  if (typeof record.modal === 'object' && record.modal)
    return true
  const name = String(record.name ?? record.connectorName ?? '').toLowerCase()
  return name.includes('walletconnect') || name.includes('appkit') || name.includes('reown')
}

function unwrapProvider(provider?: Eip1193Request): Eip1193Request | undefined {
  if (!provider)
    return undefined
  const seen = new Set<Eip1193Request>()
  let current: Eip1193Request | undefined = provider
  while (current && !seen.has(current)) {
    seen.add(current)
    const inner = current.provider
    if (inner && typeof inner.request === 'function' && inner !== current) {
      current = inner
      continue
    }
    break
  }
  return current
}

function isNamedInjectedWallet(provider: Eip1193Request): boolean {
  return Boolean(
    provider.isMetaMask
    || provider.isRabby
    || provider.isCoinbaseWallet
    || provider.isBraveWallet
    || provider.isOkxWallet
    || provider.isTrust
    || provider.isRainbow
    || provider.isPhantom,
  )
}

function listEip6963Providers(): Eip1193Request[] {
  if (typeof window === 'undefined')
    return []
  const found: Eip1193Request[] = []
  const onAnnounce = (event: Event) => {
    const provider = (event as CustomEvent<{ provider?: Eip1193Request }>).detail?.provider
    if (provider && typeof provider.request === 'function')
      found.push(provider)
  }
  window.addEventListener('eip6963:announceProvider', onAnnounce)
  window.dispatchEvent(new Event('eip6963:requestProvider'))
  window.removeEventListener('eip6963:announceProvider', onAnnounce)
  return found
}

function listInjectedEip1193Providers(exclude?: Eip1193Request): Eip1193Request[] {
  const injected = getWindowEthereum()
  const seen = new Set<Eip1193Request>()
  const out: Eip1193Request[] = []
  for (const item of [
    ...listEip6963Providers(),
    ...(Array.isArray(injected?.providers) ? injected.providers : []),
    injected,
  ]) {
    const unwrapped = unwrapProvider(item)
    if (!unwrapped || typeof unwrapped.request !== 'function' || seen.has(unwrapped))
      continue
    if (exclude && (unwrapped === exclude || unwrapped === unwrapProvider(exclude)))
      continue
    if (isAppKitOrWalletConnectProvider(unwrapped) || isAppKitOrWalletConnectProvider(item))
      continue
    seen.add(unwrapped)
    out.push(unwrapped)
  }
  return out.sort((a, b) => Number(isNamedInjectedWallet(b)) - Number(isNamedInjectedWallet(a)))
}

/** Real browser-injected wallet. Never return AppKit/WalletConnect — they fail createPresale simulation. */
export function getInjectedEip1193Provider(exclude?: Eip1193Request): Eip1193Request | undefined {
  return listInjectedEip1193Providers(exclude)[0]
}

export const PRESALE_LAUNCH_FEE_WEI = parseEthToWei(String(PRESALE_LAUNCH_FEE_ETH))
/** Test-mode factory launchFee(): 0.00001 ETH. Live PresaleFactory still returns this while production is false. */
/** Live-release presale launch fee fallback: matches production 0.1 ETH, used until the on-chain read resolves. */
export const TEST_PRESALE_LAUNCH_FEE_WEI = 10n ** 17n
export const MAX_PRESALE_NAME_LEN = 64
export const MAX_PRESALE_TICKER_LEN = 12
export const MAX_PRESALE_IMAGE_LEN = 512

export function durationDaysToSeconds(durationDays: number): bigint {
  return BigInt(Math.max(1, Math.round(durationDays))) * 24n * 60n * 60n
}

export function startDelayToSeconds(startDelaySeconds: number): bigint {
  return BigInt(Math.max(0, Math.round(startDelaySeconds || 0)))
}

const PRESALE_MIN_WINDOW_SECONDS = 24n * 60n * 60n
const PRESALE_MAX_WINDOW_SECONDS = 30n * 24n * 60n * 60n
/** Factory setting: startsAt must be at least 20 minutes from chain time. */
const PRESALE_START_BUFFER = BigInt(PRESALE_START_BUFFER_SECONDS)
/** createPresale deploys Token + Presale; Robinhood eth_call often cannot simulate that. */
export const PRESALE_CREATE_GAS = 8_000_000n
export const PRESALE_ACTION_GAS = 500_000n
/** Finalize seeds Uniswap v4 liquidity. Pre-set so Phantom never eth_estimates. */
export const PRESALE_FINALIZE_GAS = 1_500_000n

/** Factory requires startsAt >= block.timestamp. Prefer chain time and keep the picked duration. */
export async function presaleWindowUnix(input: CreateSimPresaleInput, client?: PublicClient) {
  const reader = client ?? getRobinhoodPublicClient()
  let chainNow = BigInt(Math.floor(Date.now() / 1000))
  try {
    const block = await reader.getBlock({ blockTag: 'latest' })
    if (block?.timestamp && block.timestamp > 0n)
      chainNow = block.timestamp
  }
  catch {
    // Fall back to a buffered local clock if RPC is slow.
  }
  const earliestStart = chainNow + PRESALE_START_BUFFER
  const pickedStart = input.startsAtUnix && input.startsAtUnix > 0n ? input.startsAtUnix : 0n
  const pickedEnd = input.endsAtUnix && input.endsAtUnix > 0n ? input.endsAtUnix : 0n
  const fallbackLength = durationDaysToSeconds(input.durationDays)
  const pickedLength = pickedStart > 0n && pickedEnd > pickedStart ? pickedEnd - pickedStart : fallbackLength
  const startsAt = pickedStart >= earliestStart
    ? pickedStart
    : earliestStart
  let endsAt = startsAt + (pickedLength > 0n ? pickedLength : fallbackLength)
  if (endsAt <= startsAt)
    throw new Error('End date must be after the start date.')
  if (endsAt - startsAt < PRESALE_MIN_WINDOW_SECONDS)
    endsAt = startsAt + PRESALE_MIN_WINDOW_SECONDS
  const length = endsAt - startsAt
  if (length > PRESALE_MAX_WINDOW_SECONDS)
    throw new Error('Presale window cannot be longer than 30 days.')
  return { startsAt, endsAt }
}

export function createPresaleArgs(
  input: CreateSimPresaleInput,
  window: { startsAt: bigint, endsAt: bigint },
) {
  const listingRate = input.listingRateWei && input.listingRateWei > 0n
    ? input.listingRateWei
    : input.tokensPerEthWei
  if (listingRate <= 0n || listingRate > input.tokensPerEthWei)
    throw new Error('Listing rate must be greater than 0 and not higher than tokens per ETH.')
  return [
    input.name,
    input.ticker,
    input.imageUrl ?? '',
    input.tokensPerEthWei,
    listingRate,
    window.startsAt,
    window.endsAt,
    BigInt(creatorPctFromLp(input.lpPct)),
    input.softCapWei,
    input.hardCapWei,
    input.minContributionWei,
    input.maxContributionWei,
  ] as const
}

export async function encodeCreatePresaleCalldata(input: CreateSimPresaleInput, client?: PublicClient): Promise<Hex> {
  const window = await presaleWindowUnix(input, client)
  return encodeFunctionData({
    abi: presaleFactoryAbi,
    functionName: 'createPresale',
    args: createPresaleArgs(input, window),
  })
}

export function encodeContributeCalldata(): Hex {
  return encodeFunctionData({
    abi: presaleAbi,
    functionName: 'contribute',
  })
}

export function encodeWithdrawCalldata(amount: bigint): Hex {
  return encodeFunctionData({
    abi: presaleAbi,
    functionName: 'withdraw',
    args: [amount],
  })
}

export function encodeFinalizeCalldata(): Hex {
  return encodeFunctionData({
    abi: presaleAbi,
    functionName: 'finalize',
  })
}

export function encodeClaimCalldata(): Hex {
  return encodeFunctionData({
    abi: presaleAbi,
    functionName: 'claim',
  })
}

export function encodeRefundCalldata(): Hex {
  return encodeFunctionData({
    abi: presaleAbi,
    functionName: 'refund',
  })
}

export function encodeCancelCalldata(): Hex {
  return encodeFunctionData({
    abi: presaleAbi,
    functionName: 'cancel',
  })
}

export async function readPresaleLaunchFee(client?: PublicClient): Promise<bigint> {
  const factory = getPresaleFactoryAddress()
  if (!factory)
    throw new Error('Presale factory address is not set')
  const reader = client ?? getRobinhoodPublicClient()
  try {
    return await reader.readContract({
      address: factory,
      abi: presaleFactoryAbi,
      functionName: 'launchFee',
    })
  }
  catch {
    return TEST_PRESALE_LAUNCH_FEE_WEI
  }
}

export async function readPresaleMinSoftCap(client?: PublicClient): Promise<bigint> {
  const factory = getPresaleFactoryAddress()
  if (!factory)
    throw new Error('Presale factory address is not set')
  const reader = client ?? getRobinhoodPublicClient()
  try {
    return await reader.readContract({
      address: factory,
      abi: presaleFactoryAbi,
      functionName: 'minSoftCap',
    })
  }
  catch {
    return 0n
  }
}

export async function buildCreatePresaleTx(input: CreateSimPresaleInput, feeWei: bigint, client?: PublicClient) {
  const factory = getPresaleFactoryAddress()
  if (!factory)
    throw new Error('Presale factory address is not set')
  await assertCreatePresaleAllowed(input, feeWei, client)
  const window = await presaleWindowUnix(input, client)
  return {
    request: {
      address: factory,
      abi: presaleFactoryAbi,
      functionName: 'createPresale' as const,
      args: createPresaleArgs(input, window),
      value: feeWei,
    },
    window,
  }
}

type PresaleWalletRequest = {
  address: Address
  data: Hex
  value?: bigint
  gas: bigint
}

async function resolveSenderAddress(wallet?: WalletClient, provider?: Eip1193Request): Promise<Address> {
  const fromWallet = wallet?.account?.address
  if (fromWallet)
    return fromWallet
  const requestFn = provider?.request
    ?? (typeof wallet?.request === 'function' ? (args: { method: string, params?: unknown[] }) => wallet.request(args as never) : undefined)
  if (!requestFn)
    throw new Error('Connect a wallet before continuing')
  const accounts = await requestFn({ method: 'eth_accounts' }).catch(() => []) as unknown
  const first = Array.isArray(accounts) ? accounts[0] : undefined
  if (typeof first !== 'string' || !first)
    throw new Error('Connect a wallet before continuing')
  return first as Address
}

async function fillPresaleWalletTx(
  wallet: WalletClient | undefined,
  request: PresaleWalletRequest,
  client?: PublicClient,
  provider?: Eip1193Request,
) {
  const account = await resolveSenderAddress(wallet, provider)
  const reader = client ?? getRobinhoodPublicClient()
  const nonce = await reader.getTransactionCount({ address: account, blockTag: 'pending' })
  const fees = await reader.estimateFeesPerGas().catch(() => undefined)
  const { maxFeePerGas, maxPriorityFeePerGas } = normalizedEip1559Fees(
    fees?.maxFeePerGas,
    fees?.maxPriorityFeePerGas,
  )
  return {
    account,
    chain: wallet?.chain ?? robinhoodViemChain,
    to: request.address,
    data: request.data,
    value: request.value ?? 0n,
    gas: request.gas,
    nonce,
    maxFeePerGas,
    maxPriorityFeePerGas,
  }
}

function eip1193Request(wallet: WalletClient | undefined, provider?: Eip1193Request) {
  if (provider?.request)
    return (args: { method: string, params?: unknown[] }) => provider.request(args)
  const transport = wallet?.transport as { request?: Eip1193Request['request'], value?: Eip1193Request } | undefined
  if (typeof transport?.request === 'function')
    return (args: { method: string, params?: unknown[] }) => transport.request!(args)
  if (typeof transport?.value?.request === 'function')
    return (args: { method: string, params?: unknown[] }) => transport.value!.request(args)
  if (typeof wallet?.request === 'function')
    return (args: { method: string, params?: unknown[] }) => wallet.request(args as never)
  throw new Error('Connect a wallet before continuing')
}

function sendRawPresaleTx(
  requestFn: (args: { method: string, params?: unknown[] }) => Promise<unknown>,
  params: Record<string, string>,
) {
  return requestFn({
    method: 'eth_sendTransaction',
    params: [params],
  }) as Promise<Hash>
}

/** Fallback fees so AppKit never eth_estimateGas / eth_call-simulates a send. */
export const WALLET_TX_FALLBACK_MAX_FEE_PER_GAS = 10_000_000_000n // 10 gwei
export const WALLET_TX_FALLBACK_MAX_PRIORITY_FEE_PER_GAS = 2_000_000_000n // 2 gwei

function normalizedEip1559Fees(maxFeePerGas?: bigint, maxPriorityFeePerGas?: bigint) {
  const priority = maxPriorityFeePerGas && maxPriorityFeePerGas > 0n
    ? maxPriorityFeePerGas
    : WALLET_TX_FALLBACK_MAX_PRIORITY_FEE_PER_GAS
  let maxFee = maxFeePerGas && maxFeePerGas > 0n
    ? maxFeePerGas
    : WALLET_TX_FALLBACK_MAX_FEE_PER_GAS
  if (maxFee < priority)
    maxFee = priority
  return { maxFeePerGas: maxFee, maxPriorityFeePerGas: priority }
}

/** Fully-formed EIP-1559 send params. Always include gas + fees; never omit them. */
export function walletTxParams(
  from: Address,
  request: PresaleWalletRequest,
  extras?: { nonce?: number | bigint, maxFeePerGas?: bigint, maxPriorityFeePerGas?: bigint },
): Record<string, string> {
  const gas = numberToHex(request.gas)
  const { maxFeePerGas, maxPriorityFeePerGas } = normalizedEip1559Fees(
    extras?.maxFeePerGas,
    extras?.maxPriorityFeePerGas,
  )
  const params: Record<string, string> = {
    from,
    to: request.address,
    data: request.data,
    gas,
    gasLimit: gas,
    chainId: ROBINHOOD_CHAIN_HEX,
    type: '0x2',
    maxFeePerGas: numberToHex(maxFeePerGas),
    maxPriorityFeePerGas: numberToHex(maxPriorityFeePerGas),
    value: numberToHex(request.value && request.value > 0n ? request.value : 0n),
  }
  if (extras?.nonce !== undefined)
    params.nonce = numberToHex(extras.nonce)
  return params
}

async function createPresaleSendParams(
  from: Address,
  request: PresaleWalletRequest,
  client?: PublicClient,
): Promise<Record<string, string>> {
  const reader = client ?? getRobinhoodPublicClient()
  const [nonce, fees] = await Promise.all([
    withTimeout(
      reader.getTransactionCount({ address: from, blockTag: 'pending' }),
      2_000,
      'Request timed out',
    ).catch(() => undefined),
    withTimeout(reader.estimateFeesPerGas(), 2_000, 'Request timed out').catch(() => undefined),
  ])
  return walletTxParams(from, request, {
    nonce,
    maxFeePerGas: fees?.maxFeePerGas,
    maxPriorityFeePerGas: fees?.maxPriorityFeePerGas,
  })
}

type WalletRequestFn = (args: { method: string, params?: unknown[] }) => Promise<unknown>

/** Skip silent extra wallets quickly; keep send waits long enough for a real confirm sheet. */
export const WALLET_ACCOUNT_PROBE_MS = 1_500
export const WALLET_SEND_TIMEOUT_MS = 120_000

function sameWalletAddress(left: string, right: string) {
  return left.toLowerCase() === right.toLowerCase()
}

async function listProviderAccounts(requestFn: WalletRequestFn): Promise<string[]> {
  const accounts = await withTimeout(
    requestFn({ method: 'eth_accounts' }),
    WALLET_ACCOUNT_PROBE_MS,
    'Wallet did not respond',
  ).catch(() => [])
  if (!Array.isArray(accounts))
    return []
  return accounts.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

function createPresaleSenderCandidates(wallet?: WalletClient, provider?: Eip1193Request): WalletRequestFn[] {
  const senders: WalletRequestFn[] = []
  const seen = new Set<Eip1193Request>()
  const add = (candidate?: Eip1193Request | null) => {
    const next = unwrapProvider(candidate ?? undefined)
    if (!next || typeof next.request !== 'function' || seen.has(next))
      return
    seen.add(next)
    senders.push(args => next.request(args))
  }
  // AppKit still eth_call-simulates createPresale. Prefer the unwrapped injected wallet first.
  for (const injected of listInjectedEip1193Providers())
    add(injected)
  const unwrapped = unwrapProvider(provider)
  if (unwrapped && !isAppKitOrWalletConnectProvider(unwrapped))
    add(unwrapped)
  if (senders.length === 0)
    add(provider)
  return senders
}

async function createPresaleSenders(
  from: Address,
  wallet?: WalletClient,
  provider?: Eip1193Request,
): Promise<WalletRequestFn[]> {
  const candidates = createPresaleSenderCandidates(wallet, provider)
  const ranked = await Promise.all(candidates.map(async (sender) => {
    const accounts = await listProviderAccounts(sender)
    return {
      sender,
      matched: accounts.some(account => sameWalletAddress(account, from)),
    }
  }))
  const matched = ranked.filter(item => item.matched).map(item => item.sender)
  // Only send through wallets that already hold the connected account.
  // Falling through unmatched extensions used to sit on the 120s confirm timeout.
  if (matched.length > 0)
    return matched
  return candidates.slice(0, 1)
}

/**
 * createPresale deploys Token + Presale. Send via injected EIP-1193 with gas already set so
 * AppKit / WalletConnect never simulate the two-contract deploy with eth_call.
 */
export async function sendCreatePresaleWalletTx(
  request: PresaleWalletRequest,
  account?: Address,
  wallet?: WalletClient,
  provider?: Eip1193Request,
  client?: PublicClient,
): Promise<Hash> {
  const from = account ?? await resolveSenderAddress(wallet, provider)
  const params = await createPresaleSendParams(from, request, client)
  const senders = await createPresaleSenders(from, wallet, provider)
  if (senders.length === 0)
    throw new Error('Connect a wallet before continuing')
  let lastError: unknown
  for (const sender of senders) {
    try {
      return await withTimeout(
        sendRawPresaleTx(sender, params),
        WALLET_SEND_TIMEOUT_MS,
        'Wallet did not confirm this transaction. Check your wallet, then retry.',
      )
    }
    catch (error) {
      if (isUserRejectedError(error))
        throw error
      lastError = error
    }
  }
  throw lastError
}

/** Raw eth_sendTransaction with gas already set. Prefer injected wallets so AppKit never eth_call-simulates. */
export async function sendPresaleWalletTx(
  wallet: WalletClient | undefined,
  request: PresaleWalletRequest,
  client?: PublicClient,
  provider?: Eip1193Request,
): Promise<Hash> {
  return sendCreatePresaleWalletTx(request, undefined, wallet, provider, client)
}

export async function buildCreatePresaleWalletRequest(input: CreateSimPresaleInput, feeWei: bigint, client?: PublicClient) {
  const { request, window } = await buildCreatePresaleTx(input, feeWei, client)
  return {
    request: {
      address: request.address,
      data: encodeFunctionData({
        abi: request.abi,
        functionName: request.functionName,
        args: request.args,
      }),
      value: request.value,
      gas: PRESALE_CREATE_GAS,
    },
    window,
  }
}

export type LivePresaleLimits = {
  minContribution: bigint
  maxContribution: bigint
  hardCap: bigint
  raised: bigint
  raisedKnown: boolean
  pledged: bigint
  pledgedKnown: boolean
  startsAt: bigint
  endsAt: bigint
  finalized: boolean
  status: number
  cancelled: boolean
  chainNow: bigint
}

const contributionLookupAbi = [
  ...presaleAbi,
  {
    type: 'function',
    name: 'contributions',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'contribution',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

async function readAccountPledged(
  reader: PublicClient,
  presale: Address,
  account: Address,
): Promise<{ pledged: bigint, pledgedKnown: boolean }> {
  const lookups = ['contributed', 'contributions', 'contribution'] as const
  for (const functionName of lookups) {
    try {
      const pledged = await reader.readContract({
        address: presale,
        abi: contributionLookupAbi,
        functionName,
        args: [account],
      })
      if (typeof pledged === 'bigint')
        return { pledged, pledgedKnown: true }
    }
    catch {
      // Try the next known contribution getter.
    }
  }
  return { pledged: 0n, pledgedKnown: false }
}

export async function readLivePresaleLimits(
  presale: Address,
  account?: Address,
  client?: PublicClient,
): Promise<LivePresaleLimits> {
  const reader = client ?? getRobinhoodPublicClient()
  const [
    minContribution,
    maxContribution,
    hardCap,
    raised,
    pledgedResult,
    startsAt,
    endsAt,
    finalized,
    status,
    cancelled,
    block,
  ] = await Promise.all([
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'minContribution' }).catch(() => 0n),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'maxContribution' }).catch(() => 0n),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'hardCap' }).catch(() => 0n),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'raised' }).catch(() => undefined),
    account ? readAccountPledged(reader, presale, account) : Promise.resolve({ pledged: 0n, pledgedKnown: false }),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'startsAt' }).catch(() => 0n),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'endsAt' }).catch(() => 0n),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'finalized' }).catch(() => false),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'status' }).catch(() => 1),
    reader.readContract({ address: presale, abi: presaleAbi, functionName: 'cancelled' }).catch(() => false),
    reader.getBlock({ blockTag: 'latest' }).catch(() => undefined),
  ])
  const raisedKnown = typeof raised === 'bigint'
  const raisedWei = raisedKnown ? raised : 0n
  const nothingRaised = raisedKnown && raisedWei === 0n
  return {
    minContribution,
    maxContribution,
    hardCap,
    raised: raisedWei,
    raisedKnown,
    pledged: nothingRaised ? 0n : pledgedResult.pledged,
    pledgedKnown: nothingRaised ? true : pledgedResult.pledgedKnown,
    startsAt,
    endsAt,
    finalized: Boolean(finalized),
    status: Number(status ?? 1),
    cancelled: Boolean(cancelled),
    chainNow: block?.timestamp ?? BigInt(Math.floor(Date.now() / 1000)),
  }
}

function remainingHardCap(hardCap: bigint, raised: bigint): bigint {
  return hardCap > raised ? hardCap - raised : 0n
}

export function isLivePresaleWindow(limits: Pick<LivePresaleLimits, 'startsAt' | 'endsAt' | 'finalized' | 'status' | 'cancelled' | 'chainNow'>): boolean {
  if (limits.cancelled || limits.finalized || limits.status === 2 || limits.status === 3 || limits.status === 4)
    return false
  if (limits.startsAt > 0n && limits.chainNow < limits.startsAt)
    return false
  if (limits.endsAt > 0n && limits.chainNow > limits.endsAt)
    return false
  return true
}

/** Withdraw stays open until end, including before start, and after creator cancel. */
export function isWithdrawPresaleWindow(limits: Pick<LivePresaleLimits, 'endsAt' | 'finalized' | 'status' | 'cancelled' | 'chainNow'>): boolean {
  if (limits.cancelled)
    return true
  if (limits.finalized || limits.status === 2 || limits.status === 3 || limits.status === 4)
    return false
  if (limits.endsAt > 0n && limits.chainNow > limits.endsAt)
    return false
  return true
}

/** Contract contribute() requires msg.value >= minContribution() on every pledge. */
export function assertAmountMeetsMin(amountWei: bigint, minContribution: bigint) {
  if (amountWei <= 0n)
    throw new Error('Enter an amount greater than 0.')
  if (minContribution > 0n && amountWei < minContribution)
    throw new Error(`Contribution must be at least ${weiToEthString(minContribution, 18)} ETH (on-chain minContribution).`)
}

export async function assertContributeAllowed(
  presale: Address,
  amountWei: bigint,
  account: Address,
  client?: PublicClient,
) {
  if (amountWei <= 0n)
    throw new Error('Enter an amount greater than 0.')

  const reader = client ?? getRobinhoodPublicClient()
  let limits: LivePresaleLimits | null = null
  try {
    limits = await withTimeout(
      readLivePresaleLimits(presale, account, reader),
      2_000,
      'Request timed out',
    )
  }
  catch {
    limits = null
  }
  const liveKnown = Boolean(limits && (limits.hardCap > 0n || limits.startsAt > 0n || limits.endsAt > 0n))
  if (liveKnown && limits && !isLivePresaleWindow(limits))
    throw new Error('This presale is not live on-chain yet, or it has already closed.')
  if (limits && limits.hardCap > 0n && limits.raised + amountWei > limits.hardCap)
    throw new Error('This amount would exceed the hard cap.')
  const remaining = limits ? remainingHardCap(limits.hardCap, limits.raised) : 0n
  const fillsHardCap = remaining > 0n && amountWei === remaining
  if (!fillsHardCap)
    assertAmountMeetsMin(amountWei, limits?.minContribution ?? 0n)
  if (limits && limits.maxContribution > 0n && limits.pledgedKnown && limits.pledged + amountWei > limits.maxContribution)
    throw new Error('This amount would exceed the per-wallet maximum.')
}

export async function assertCreatePresaleAllowed(
  input: CreateSimPresaleInput,
  feeWei: bigint,
  client?: PublicClient,
) {
  if (feeWei <= 0n)
    throw new Error('Factory launchFee() does not match the ETH sent with this create.')
  const listingRate = input.listingRateWei && input.listingRateWei > 0n
    ? input.listingRateWei
    : input.tokensPerEthWei
  if (listingRate <= 0n || listingRate > input.tokensPerEthWei)
    throw new Error('Listing rate must be greater than 0 and not higher than tokens per ETH.')
  if (input.hardCapWei > input.softCapWei * 4n)
    throw new Error('Hard cap cannot be more than 4× the soft cap.')
  if (input.minContributionWei * input.tokensPerEthWei / 10n ** 18n === 0n)
    throw new Error('Min contribution must mint at least 1 token at the presale rate.')
  const minSoftCap = await readPresaleMinSoftCap(client)
  if (minSoftCap > 0n && input.softCapWei < minSoftCap)
    throw new Error('Soft cap is below the factory minimum.')
  await presaleWindowUnix(input, client)
}

function walletRequest(address: Address, data: Hex, gas: bigint, value?: bigint): PresaleWalletRequest {
  return { address, data, value, gas }
}

export function buildContributeTx(presale: Address, amountWei: bigint) {
  return {
    address: presale,
    abi: presaleAbi,
    functionName: 'contribute' as const,
    args: [] as const,
    value: amountWei,
  }
}

export function buildContributeWalletRequest(presale: Address, amountWei: bigint) {
  return walletRequest(presale, encodeContributeCalldata(), PRESALE_ACTION_GAS, amountWei)
}

export function buildWithdrawWalletRequest(presale: Address, amountWei: bigint) {
  return walletRequest(presale, encodeWithdrawCalldata(amountWei), PRESALE_ACTION_GAS)
}

export function buildFinalizeWalletRequest(presale: Address) {
  return walletRequest(presale, encodeFinalizeCalldata(), PRESALE_FINALIZE_GAS, 0n)
}

export function buildClaimWalletRequest(presale: Address) {
  return walletRequest(presale, encodeClaimCalldata(), PRESALE_ACTION_GAS)
}

export function buildRefundWalletRequest(presale: Address) {
  return walletRequest(presale, encodeRefundCalldata(), PRESALE_ACTION_GAS)
}

export function buildCancelWalletRequest(presale: Address) {
  return walletRequest(presale, encodeCancelCalldata(), PRESALE_ACTION_GAS)
}

export function buildWithdrawTx(presale: Address, amountWei: bigint) {
  return {
    address: presale,
    abi: presaleAbi,
    functionName: 'withdraw' as const,
    args: [amountWei] as const,
  }
}

export function buildFinalizeTx(presale: Address) {
  return {
    address: presale,
    abi: presaleAbi,
    functionName: 'finalize' as const,
    args: [] as const,
  }
}

export function buildClaimTx(presale: Address) {
  return {
    address: presale,
    abi: presaleAbi,
    functionName: 'claim' as const,
    args: [] as const,
  }
}

export function buildRefundTx(presale: Address) {
  return {
    address: presale,
    abi: presaleAbi,
    functionName: 'refund' as const,
    args: [] as const,
  }
}

export function buildCancelTx(presale: Address) {
  return {
    address: presale,
    abi: presaleAbi,
    functionName: 'cancel' as const,
    args: [] as const,
  }
}

export function parsePresaleCreatedAddress(logs: readonly Log[]): Address | undefined {
  const parsed = parseEventLogs({
    abi: presaleFactoryAbi,
    eventName: 'PresaleCreated',
    logs,
  })
  return parsed[0]?.args?.presale
}

export function parsePresaleCreatedToken(logs: readonly Log[]): Address | undefined {
  const parsed = parseEventLogs({
    abi: presaleFactoryAbi,
    eventName: 'PresaleCreated',
    logs,
  })
  return parsed[0]?.args?.token
}
